import type {ComponentType} from "react";
import {lazy} from "react";
import {CHUNK_RELOAD_KEY, isChunkLoadError} from "./chunkLoad.util";

function getSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

let reloadTriggered = false;

/**
 * Drop-in replacement for React.lazy that handles stale chunk errors after deployments.
 *
 * When Vite builds a new version, old JS chunks are replaced. Browsers with stale tabs
 * (especially mobile bfcache) try to load chunks that no longer exist -> 404.
 * This wrapper catches known chunk-load errors and reloads the page once.
 * A sessionStorage flag prevents infinite reload loops; if a reload was already attempted
 * and the import still fails, the error is re-thrown for observability.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  importFn: () => Promise<{default: T}>
) {
  return lazy(() =>
    importFn()
      .then((module) => {
        // If a previous chunk-reload succeeded, clear the flag for future deployments.
        const storage = getSessionStorage();
        storage?.removeItem(CHUNK_RELOAD_KEY);
        reloadTriggered = false;
        return module;
      })
      .catch((error) => {
        if (!isChunkLoadError(error)) {
          throw error;
        }

        const storage = getSessionStorage();
        const alreadyReloaded = storage?.getItem(CHUNK_RELOAD_KEY) === "1";

        if (!alreadyReloaded) {
          storage?.setItem(CHUNK_RELOAD_KEY, "1");
          reloadTriggered = true;
          window.location.reload();
          // Never resolve while browser is reloading.
          return new Promise<{default: T}>(() => {});
        }

        // Another lazy import may fail before navigation happens; keep waiting for the reload.
        if (reloadTriggered) {
          return new Promise<{default: T}>(() => {});
        }

        // Reload already attempted and still failing: clear flag and propagate.
        storage?.removeItem(CHUNK_RELOAD_KEY);
        throw error;
      })
  );
}
